import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, RendererFactory2 } from '@angular/core';

import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class JsLoader {
  private readonly renderer = this.rendererFactory.createRenderer(null, null);

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly rendererFactory: RendererFactory2
  ) {}

  /**
   * This function is being used to load a JavaScript file into Dom
   * Please use only in cases that are necessary
   * @param  {string} src the javascript file source
   * @param  {string} id optional - the script id
   * @returns Observable<HTMLScriptElement> which will contain the javascript id or throw an error.
   */
  public loadScript$(src: string, id?: string): Observable<HTMLScriptElement> {
    return new Observable<HTMLScriptElement>(subs => {
      const scriptTag = this.getScriptTag(src, id);

      /* Check to see if the script has already been loaded*/
      if (scriptTag) {
        subs.next(scriptTag);
        subs.complete();
      } else {
        const script: HTMLScriptElement = this.renderer.createElement('script');
        script.src = src;
        if (id) {
          script.id = id;
        }

        this.renderer.appendChild(this.document.head, script);

        script.onload = () => {
          subs.next(script);
          subs.complete();
        };
        script.onerror = () => {
          subs.error(`Script ${script.id} could not be loaded.`);
          this.renderer.removeChild(this.renderer.parentNode(script), script);
        };
      }
    });
  }

  public getScriptTag(src: string, id?: string): HTMLScriptElement {
    return id
      ? (this.document.getElementById(id) as HTMLScriptElement)
      : this.document.querySelector(`script[src="${src}"]`);
  }

  /**
   * @param  {HTMLScriptElement} script required, the script element received from load script.
   * @returns void
   */
  public unloadScript(script: HTMLScriptElement): void {
    if (script && this.renderer.parentNode(script)) {
      this.renderer.removeChild(this.renderer.parentNode(script), script);
    }
  }
}
